import api from '@/services/api/client';
import {
  PlayerDocumentUploadV2,
  playerDocumentsV2API,
} from '@/services/api/playerDocumentsV2';
import {Capability, V2BrowseResponse, V2DetailResponse} from '@/services/api/v2';
import {PlayerBrowseState, PlayerReportType, PlayerTab} from './types';

export type {PlayerReportType} from './types';

export type PlayerScope = 'squad' | 'external';
export type PlayerAvailability = 'fit' | 'injured' | 'recovering' | 'questionable';
export type PlayerSquadStatus = 'active' | 'review' | 'archived';

interface PlayerBrowseDTO {
  id: string;
  scope: PlayerScope;
  name: string;
  age: number;
  position: string;
  team: string | null;
  photo_url: string | null;
  availability: PlayerAvailability;
  latest_rating: number;
  rating_source: string;
  last_report_at: string | null;
  report_count: number;
  squad_status: PlayerSquadStatus;
  squad_review_reason: string | null;
  squad_review_started_at: string | null;
  is_watched: boolean;
  capabilities: Capability[];
  wyscout_available: boolean;
  limited_data: boolean;
}

export interface PlayerBrowseItem {
  id: string;
  scope: PlayerScope;
  name: string;
  age: number;
  position: string;
  team: string | null;
  photoUrl: string | null;
  availability: PlayerAvailability;
  latestRating: number;
  ratingSource: string;
  lastReportAt: string | null;
  reportCount: number;
  squadStatus: PlayerSquadStatus;
  squadReviewReason: string | null;
  squadReviewStartedAt: string | null;
  isWatched: boolean;
  capabilities: Capability[];
  wyscoutAvailable: boolean;
  limitedData: boolean;
}

export interface PlayerFacet {
  value: string;
  count: number;
}

export interface PlayerBrowseFacets {
  positions: PlayerFacet[];
  teams: PlayerFacet[];
  availability: PlayerFacet[];
}

export interface PlayerBrowseResponse {
  data: PlayerBrowseItem[];
  meta: {page: number; pageSize: number; total: number; totalPages: number};
  facets: PlayerBrowseFacets;
  capabilities: Capability[];
}

export interface SquadSyncResult {
  added: number;
  updated: number;
  reactivated: number;
  review: number;
  ignored: number;
  unchanged: number;
}

interface SquadSyncRunDTO extends SquadSyncResult {
  id: string;
  workspaceId: string;
  wyscoutTeamId: number;
  trigger: 'manual' | 'scheduled';
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  completedAt: string | null;
  failureSummary: string | null;
}

export interface SquadSyncSummary extends SquadSyncResult {
  runId: string;
  workspaceId: string;
  wyscoutTeamId: number;
  trigger: 'manual' | 'scheduled';
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  completedAt: string | null;
  failureSummary: string | null;
}

interface SquadReviewPlayerDTO {
  id: string;
  name: string;
  age: number;
  position: string;
  scope: 'squad';
  squadStatus: 'review';
  reviewReason: string | null;
  wyscoutStatus: WyscoutReviewStatus;
  reviewStartedAt: string | null;
  wyscoutPlayerId: number | null;
  team: string | null;
  photoUrl: string | null;
}

export type WyscoutReviewStatus = 'Missing' | 'Inactive' | 'Different team' | 'Needs review';

export interface SquadReviewPlayer {
  id: string;
  name: string;
  age: number;
  position: string;
  team: string | null;
  photoUrl: string | null;
  squadStatus: 'review';
  reason: string | null;
  wyscoutStatus: WyscoutReviewStatus;
  reviewStartedAt: string | null;
  wyscoutPlayerId: number | null;
}

export type SquadReviewAction = 'archive' | 'keep_active';
export interface SquadReviewDecisionResult {archived: number; keptActive: number}

export interface PlayerMutationInput {
  scope: PlayerScope;
  name: string;
  age: number;
  position: 'Forward' | 'Midfielder' | 'Defender' | 'Goalkeeper';
  team?: string;
  photoUrl?: string;
  photoFile?: File;
}

export interface PlayerDetail {
  id: string;
  wyscout_player_id: number | null;
  scope: PlayerScope;
  name: string;
  age: number;
  position: string;
  team: string | null;
  photo_url: string | null;
  birth_date: string | null;
  height: number | null;
  foot: string | null;
  nationality: string | null;
  availability: PlayerAvailability;
  is_watched: boolean;
  headline_metrics: {
    latest_rating: number;
    rating_source: string;
    report_count: number;
    last_report_at: string | null;
  };
  tabs: PlayerTab[];
  readable_report_types: PlayerReportType[];
}

export interface PlayerMutationResult {
  id: string;
  scope: PlayerScope;
}

export interface PlayerComparisonWyscoutMetrics {
  sample_size: number;
  appearances: number;
  minutes: number;
  goals: number;
  assists: number;
  shots: number;
  shots_on_target: number;
  expected_goals: number;
  expected_assists: number;
  passes: number;
  successful_passes: number;
  duels: number;
  duels_won: number;
  interceptions: number;
  recoveries: number;
  goals_per_90: number | null;
  assists_per_90: number | null;
  shots_per_90: number | null;
  shots_on_target_per_90: number | null;
  expected_goals_per_90: number | null;
  expected_assists_per_90: number | null;
  passes_per_90: number | null;
  successful_passes_per_90: number | null;
  duels_per_90: number | null;
  duels_won_per_90: number | null;
  interceptions_per_90: number | null;
  recoveries_per_90: number | null;
  pass_completion: number | null;
  duel_success: number | null;
}

export interface PlayerComparisonV2Item {
  id: string;
  name: string;
  position: string;
  age: number;
  availability: string;
  scouting: {shooting: number; passing: number; pace: number; dribbling: number; defending: number; physical: number};
  performance: {average_rating_30d: number; goals: number; assists: number};
  training: {attendance_rate_30d: number; average_rpe_30d: number};
  load: {acwr: number; risk: string};
  wyscout: PlayerComparisonWyscoutMetrics | null;
}

export interface PlayerComparisonV2Benchmark {
  position: string;
  scope: PlayerScope;
  sample_size: number;
  scouting: PlayerComparisonV2Item['scouting'];
  performance: {average_rating_30d: number; goals: number; assists: number};
  training: PlayerComparisonV2Item['training'];
  load: {acwr: number};
  wyscout: PlayerComparisonWyscoutMetrics;
}

export interface PlayerComparisonV2Response {
  data: {players: PlayerComparisonV2Item[]; benchmarks: PlayerComparisonV2Benchmark[]; compared_at: string};
  capabilities: Capability[];
}

interface PlayerReportCommon {
  id: string;
  report_type: PlayerReportType;
  report_date: string | null;
  created_at: string | null;
  summary: string | null;
}

export interface PlayerPerformanceReport extends PlayerReportCommon {
  report_type: 'performance'; reporter_id: string | null; performance_kind: string;
  minutes_played: number | null; goals: number | null; assists: number | null;
  distance_covered: string | null; rating: number | null; notes: string | null;
}

export interface PlayerScoutingReport extends PlayerReportCommon {
  report_type: 'scouting'; scout_id: string | null; shooting: number | null; passing: number | null;
  pace: number | null; dribbling: number | null; defending: number | null; physical: number | null;
  comments: string | null; updated_at: string | null;
}

export interface PlayerMedicalReport extends PlayerReportCommon {
  report_type: 'medical'; staff_id: string | null; injury_type: string | null;
  status: string; severity: string; notes: string | null;
}

export interface PlayerTrainingReport extends PlayerReportCommon {
  report_type: 'training'; coach_id: string | null; attended: boolean; fitness_score: number | null;
  endurance_score: number | null; intensity_score: number | null; attitude_score: number | null;
  turned_up_on_time: boolean; notes: string | null;
}

export type PlayerReport = PlayerPerformanceReport | PlayerScoutingReport | PlayerMedicalReport | PlayerTrainingReport;

export interface PlayerReportsResponse {
  data: PlayerReport[];
  meta: {limit: number; offset: number; has_more: boolean};
}

const browseParams = (scope: PlayerScope, state: PlayerBrowseState): Record<string, string | number | boolean> => ({
  scope,
  ...(state.q ? {q: state.q} : {}),
  page: state.page,
  page_size: state.pageSize,
  sort: state.sort,
  order: state.order,
  ...(state.position ? {position: state.position} : {}),
  ...(state.team ? {team: state.team} : {}),
  ...(state.availability ? {availability: state.availability} : {}),
  ...(state.minAge !== undefined ? {min_age: state.minAge} : {}),
  ...(state.maxAge !== undefined ? {max_age: state.maxAge} : {}),
  ...(state.minRating !== undefined ? {min_rating: state.minRating} : {}),
  ...(state.maxRating !== undefined ? {max_rating: state.maxRating} : {}),
  ...(state.recentDays !== undefined ? {recent_days: state.recentDays} : {}),
  ...(state.playedLastMatch ? {played_last_match: true} : {}),
  ...(scope === 'external' && state.watching ? {watching: true} : {}),
  ...(state.squadStatus === 'archived' ? {squad_status: 'archived'} : {}),
});

const mutationBody = (input: PlayerMutationInput): PlayerMutationInput | FormData => {
  if (!input.photoFile) {
    const {photoFile, photoUrl, ...rest} = input;
    return {...rest, ...(photoUrl ? {photo_url: photoUrl} : {})} as unknown as PlayerMutationInput;
  }
  const form = new FormData();
  form.append('scope', input.scope);
  form.append('name', input.name);
  form.append('age', String(input.age));
  form.append('position', input.position);
  if (input.team) form.append('team', input.team);
  if (input.photoUrl) form.append('photo_url', input.photoUrl);
  form.append('photo', input.photoFile);
  return form;
};
const workspaceHeaders = (workspaceId?: string) => workspaceId ? {headers: {'X-Workspace-ID': workspaceId}} : {};

export const playersV2API = {
  browse: async (scope: PlayerScope, state: PlayerBrowseState, workspaceId?: string): Promise<PlayerBrowseResponse> => {
    const response = await api.get<V2BrowseResponse<PlayerBrowseDTO, PlayerBrowseFacets>>('/v2/players', {
      params: browseParams(scope, state),
      ...workspaceHeaders(workspaceId),
    });
    return {
      data: response.data.data.map(player => ({
        id: player.id,
        scope: player.scope,
        name: player.name,
        age: player.age,
        position: player.position,
        team: player.team,
        photoUrl: player.photo_url,
        availability: player.availability,
        latestRating: player.latest_rating,
        ratingSource: player.rating_source,
        lastReportAt: player.last_report_at,
        reportCount: player.report_count,
        squadStatus: player.squad_status,
        squadReviewReason: player.squad_review_reason,
        squadReviewStartedAt: player.squad_review_started_at,
        isWatched: player.is_watched,
        capabilities: player.capabilities,
        wyscoutAvailable: player.wyscout_available,
        limitedData: player.limited_data,
      })),
      meta: {
        page: response.data.meta.page,
        pageSize: response.data.meta.page_size,
        total: response.data.meta.total,
        totalPages: response.data.meta.total_pages,
      },
      facets: response.data.facets,
      capabilities: response.data.capabilities,
    };
  },

  detail: async <T = PlayerDetail>(id: string, workspaceId?: string): Promise<V2DetailResponse<T>> =>
    (await api.get(`/v2/players/${id}`, workspaceHeaders(workspaceId))).data,

  reports: async (
    id: string,
    state: {type?: PlayerReportType; page: number; pageSize: number},
    workspaceId?: string,
  ): Promise<PlayerReportsResponse> =>
    (await api.get(`/v2/players/${encodeURIComponent(id)}/reports`, {
      params: {
        limit: state.pageSize,
        offset: (state.page - 1) * state.pageSize,
        ...(state.type ? {type: state.type} : {}),
      },
      ...workspaceHeaders(workspaceId),
    })).data,

  analysis: async <T = unknown>(id: string, workspaceId?: string): Promise<{data: T}> =>
    (await api.get(`/v2/players/${id}/analysis`, workspaceHeaders(workspaceId))).data,

  compare: async (playerIDs: string[], workspaceId?: string): Promise<PlayerComparisonV2Response> =>
    (await api.post('/v2/player-comparisons', {player_ids: playerIDs}, workspaceHeaders(workspaceId))).data,

  create: async <T = PlayerMutationResult>(input: PlayerMutationInput, workspaceId?: string): Promise<{data: T}> =>
    (await api.post('/v2/players', mutationBody(input), workspaceHeaders(workspaceId))).data,

  update: async <T = PlayerMutationResult>(id: string, input: PlayerMutationInput, workspaceId?: string): Promise<{data: T}> =>
    (await api.put(`/v2/players/${id}`, mutationBody(input), workspaceHeaders(workspaceId))).data,

  updateAvailability: async <T = unknown>(id: string, availability: PlayerAvailability, workspaceId?: string): Promise<{data: T}> =>
    (await api.patch(`/v2/players/${id}/availability`, {availability}, workspaceHeaders(workspaceId))).data,

  setWatched: async (id: string, watching: boolean, workspaceId?: string): Promise<{data: {id: string; is_watched: boolean}}> =>
    watching
      ? (await api.put(`/v2/players/${encodeURIComponent(id)}/watchlist`, undefined, workspaceHeaders(workspaceId))).data
      : (await api.delete(`/v2/players/${encodeURIComponent(id)}/watchlist`, workspaceHeaders(workspaceId))).data,

  remove: async (id: string, workspaceId?: string): Promise<void> => {
    await api.delete(`/v2/players/${id}`, workspaceHeaders(workspaceId));
  },

  syncSquad: async (workspaceId: string): Promise<SquadSyncResult> =>
    (await api.post('/v2/wyscout/squad-sync', undefined, workspaceHeaders(workspaceId))).data.data,

  latestSquadSync: async (workspaceId: string): Promise<SquadSyncSummary | null> => {
    const response = await api.get<{data: SquadSyncRunDTO | null}>(
      '/v2/wyscout/squad-sync/latest', workspaceHeaders(workspaceId),
    );
    const run = response.data.data;
    return run ? {
      runId: run.id,
      workspaceId: run.workspaceId,
      wyscoutTeamId: run.wyscoutTeamId,
      trigger: run.trigger,
      status: run.status,
      added: run.added,
      updated: run.updated,
      reactivated: run.reactivated,
      review: run.review,
      ignored: run.ignored,
      unchanged: run.unchanged,
      startedAt: run.startedAt,
      completedAt: run.completedAt,
      failureSummary: run.failureSummary,
    } : null;
  },

  squadReview: async (workspaceId: string): Promise<SquadReviewPlayer[]> => {
    const response = await api.get<{data: SquadReviewPlayerDTO[]}>(
      '/v2/wyscout/squad-review', workspaceHeaders(workspaceId),
    );
    return response.data.data.map(player => ({
      id: player.id,
      name: player.name,
      age: player.age,
      position: player.position,
      team: player.team,
      photoUrl: player.photoUrl,
      squadStatus: player.squadStatus,
      reason: player.reviewReason,
      wyscoutStatus: player.wyscoutStatus,
      reviewStartedAt: player.reviewStartedAt,
      wyscoutPlayerId: player.wyscoutPlayerId,
    }));
  },

  decideSquadReview: async (
    action: SquadReviewAction,
    playerIds: string[],
    workspaceId: string,
  ): Promise<SquadReviewDecisionResult> => (await api.post(
    '/v2/wyscout/squad-review/decisions',
    {action, playerIds},
    workspaceHeaders(workspaceId),
  )).data.data,

  listDocuments: (id: string, pagination: {page?: number; pageSize?: number} = {}, workspaceId?: string) => playerDocumentsV2API.list(id, pagination, workspaceId),
  uploadDocument: (id: string, upload: PlayerDocumentUploadV2, workspaceId?: string) => playerDocumentsV2API.upload(id, upload, workspaceId),
  deleteDocument: (id: string, workspaceId?: string) => playerDocumentsV2API.delete(id, workspaceId),
  viewDocument: (id: string, workspaceId?: string) => playerDocumentsV2API.viewDocument(id, workspaceId),
  downloadDocument: (id: string, workspaceId?: string) => playerDocumentsV2API.downloadDocument(id, workspaceId),
  validateDocumentUpload: playerDocumentsV2API.validateUpload,
};
